// The sign-in sheet's state, outside React (#3928). Any surface can open it
// (the Start free buttons through useGoogleSignIn, the in-app notice, the
// return from Google), and one <SignInSheet /> at the app root draws it.
//
// Modes, from the founder's pick A (2026-10-11):
//   'choose'  normal browser: Continue with Google is the one big button,
//             "or use your email" a quiet link under it
//   'in_app'  Instagram, Facebook, LinkedIn, TikTok...: Google refuses
//             sign-in there, so the email field leads and Google is a
//             one-line way out to a real browser
//   'back'    a Google sign-in that did not finish: a Back from Google's
//             page (#3894), a return by another page or a lost one (#4117),
//             a closed popup. Offered every time (founder, 2026-10-13: offer
//             the email link straight away rather than another Google try;
//             one student tried Google 4 times over 2 days and never got in)
//   'email_first' iPhone and iPad Safari and the Home Screen app while
//             phone_email_first is on (#5084, founder pick A, 2026-10-21):
//             the email field and its send button first, "Continue with
//             Google" a full button under an "or" rule. Students on those
//             phones left at Google's own email and password form.
//   'exists'  #5154: an Apple or Facebook tap whose address already has an
//             account that signs in another way: "This email already has
//             a Stratia account", the email link to that address and the
//             other buttons (signInLink.js)
//   'refused' #5154: the password guard refused the provider (a Facebook
//             account with no email): its words, then the other ways in
//   'link'    #5154: signed in after 'exists': "Also sign in with Apple
//             next time?" Nothing is linked without the yes
//
// With more_sign_in_options on (#5154, founder pick A), every mode that
// offers providers draws the list signInProviders.providersHere() gives, in
// the platform's order; with nothing beyond Google it draws today's sheet.
import { track, EVENTS } from './analytics';
import { signinEntry } from '../content/landingIds';
import { emailSignInAnswer, loadEmailSignInAvailability, phoneEmailFirstOn } from './emailLinkSignIn';
import {
    inAppBrowserName, newSignInAttempt, signInAttemptId, signInAttemptProps, signInBrowserClass, signInPlatform,
} from './signInTrouble';
import { providersHere } from './signInProviders';

let sheet = null;
const listeners = new Set();
const emit = () => { for (const fn of listeners) { try { fn(); } catch { /* a listener never breaks another */ } } };

export const MODES = Object.freeze(['choose', 'in_app', 'back', 'email_first', 'exists', 'refused', 'link']);
// The modes that are not a sign-in start: the consent after one.
const NOT_A_START = ['link'];
// The steps a closed sheet reports (#5084): before the field, at the field,
// after a send.
export const CLOSE_STEPS = Object.freeze(['start', 'email', 'sent']);
// How long a send in an app's browser waits for get-auth-methods when the
// sheet opened before it answered: the same wait the return from Google
// gives it (RedirectSignInHandler, #4117).
export const ANSWER_WAIT_MS = 2500;

// The sheet a browser class opens to, the email link known to be on. Only
// iOS Safari's entry reads the flag; a new class is a new row here.
const MODE_FOR_CLASS = {
    in_app: () => 'in_app',
    ios_safari: () => (phoneEmailFirstOn() ? 'email_first' : 'choose'),
    other: () => 'choose',
};

/** The mode a Start free opens for this browser (#5084). */
export function sheetModeHere() {
    return (MODE_FOR_CLASS[signInBrowserClass()] || MODE_FOR_CLASS.other)();
}

/** Whether the recovery sheet opens with the field showing (#5084, A):
 *  every browser while phone_email_first is on (founder, 2026-10-22: "a").
 *  Only the recovery screen; the first sheet stays Google-first on Chrome,
 *  Android and desktop. */
const recoveryFieldFirst = () => phoneEmailFirstOn();

/** {mode, entry, app, onGoogle, dest, title, lead, everyMode}: onGoogle runs the
 *  Google sign-in itself (never this sheet again), called straight from the
 *  click so a popup keeps its user gesture. `dest`: an internal path the
 *  email link lands on, kept only when a link is actually sent (#4117).
 *  `title`/`lead`: the sheet's words when "Start free" is wrong (a buyer on
 *  /pricing; a sign-up moment's own button and payoff, #5195). `everyMode`:
 *  the in-app and recovery sheets repeat them too (a sign-up moment's words,
 *  #5195); without it those sheets keep their own, as /pricing's do. `answer`: an in-app sheet
 *  opened before get-auth-methods answered (#5084), the pending answer.
 *  `fieldFirst`: the email field shows at once and the keyboard stays down
 *  (email_first, and the recovery sheet where email leads, #5084). Every
 *  open is counted (signin_sheet_opened) and starts a new attempt, except
 *  the recovery sheet, which keeps the tab's attempt when there is one. */
export function openSignInSheet({ mode = 'choose', entry, app = null, onGoogle = null, onProvider = null, dest = null,
    title = null, lead = null, answer = null, fieldFirst = false, retry = 'google', exclude = [],
    email = '', notice = null, link = null, everyMode = false } = {}) {
    const m = MODES.includes(mode) ? mode : 'choose';
    // The recovery sheet continues the attempt it recovers (reviewer, #5084):
    // "Google did not finish, then the email link did" is one attempt; so do
    // the account-exists and refusal sheets (#5154).
    const keeps = ['back', 'exists', 'refused'].includes(m);
    const attempt = (keeps && signInAttemptId()) || (NOT_A_START.includes(m) ? null : newSignInAttempt());
    // The buttons this sheet draws, decided once at open (#5154) so the
    // screen and the event below say the same thing.
    const providers = m === 'link' ? [] : providersHere({ exclude });
    sheet = {
        mode: m, entry, app, onGoogle, onProvider, dest, title, lead, opened: Date.now(), attempt, providers,
        retry, email, notice, link, everyMode: Boolean(everyMode),
        fieldFirst: m === 'email_first' || m === 'exists' || (m === 'back' && Boolean(fieldFirst)),
        answer: answer ? { promise: answer, settled: false } : null,
    };
    if (!NOT_A_START.includes(m)) {
        try {
            track(EVENTS.SIGNIN_SHEET_OPENED, {
                mode: m, entry: signinEntry(entry), ...(app ? { app } : {}), ...signInAttemptProps(attempt),
                platform: signInPlatform(), providers: providers.join(','),
            });
        } catch { /* analytics never blocks sign-in */ }
    }
    emit();
}

/** "This email already has a Stratia account" (#5154). `info`:
 *  {provider, email} from signInLink.stashPendingLink. */
export function openAccountExistsSheet({ entry, info, onProvider = null, onGoogle = null, dest = null } = {}) {
    if (!info?.provider) return false;
    openSignInSheet({ mode: 'exists', entry, onProvider, onGoogle, dest, retry: info.provider,
        exclude: [info.provider], email: info.email || '' });
    return true;
}

/** The password guard refused this provider (#5154): its words, then the
 *  other ways in. */
export function openRefusedSheet({ entry, provider, notice, onProvider = null, onGoogle = null, dest = null } = {}) {
    openSignInSheet({ mode: 'refused', entry, onProvider, onGoogle, dest, retry: provider, exclude: [provider], notice });
    return true;
}

/** "Also sign in with Apple next time?" (#5154): `link` is
 *  {pending, user, signedInWith}. Not a sign-in start, so it opens no
 *  attempt and counts no sheet open. */
export function openLinkConsent(link) {
    if (sheet || !link?.pending) return false;
    openSignInSheet({ mode: 'link', link });
    return true;
}

/** The sheet's link went out (#5084): a late "email off" answer must not
 *  close a student's "Check your email" (security review, low). */
export function markSheetSent(s = sheet) {
    if (s) s.sent = true;
}

export function closeSignInSheet() {
    if (!sheet) return;
    sheet = null;
    emit();
}

/** The student closed the sheet (the close button, the backdrop, Escape)
 *  without choosing Google: counted with where they were (#5084). */
export function dismissSignInSheet(step = 'start') {
    if (!sheet) return;
    try {
        track(EVENTS.SIGNIN_SHEET_CLOSED, {
            mode: sheet.mode, step: CLOSE_STEPS.includes(step) ? step : 'start', ...signInAttemptProps(sheet.attempt),
        });
    } catch { /* analytics never blocks */ }
    closeSignInSheet();
}

// The in-app race (#5084): fired once per sheet, by whichever comes first,
// the answer landing or a send that gave up waiting for it.
function settleAnswer(s, outcome) {
    if (!s?.answer || s.answer.settled) return;
    s.answer.settled = true;
    try {
        track(EVENTS.SIGNIN_IN_APP_WAITED, {
            waited_ms: Math.max(0, Date.now() - s.opened), outcome, ...signInAttemptProps(s.attempt),
        });
    } catch { /* analytics never blocks */ }
}

/** Before a send from this sheet: wait (at most ANSWER_WAIT_MS) for an
 *  answer the sheet opened without. Resolves false only when the email link
 *  is known to be off (the sheet is then closed for the notice); a timeout
 *  sends anyway and lets Firebase decide. */
export async function awaitSheetAnswer(s = sheet, waitMs = ANSWER_WAIT_MS) {
    if (!s?.answer) return true;
    const TIMEOUT = Symbol('timeout');
    const got = await Promise.race([
        s.answer.promise.then((on) => on === true, () => false),
        new Promise((r) => { setTimeout(() => r(TIMEOUT), waitMs); }),
    ]);
    if (got === TIMEOUT) { settleAnswer(s, 'timeout'); return true; }
    return got;
}

/**
 * What every Start free does (#5084; invariant 18): opens the sheet for
 * this browser and returns true, or returns false when the caller should
 * go to Google itself (the email link is off, or not known yet outside an
 * app's browser: today's path, so a slow network changes nothing).
 *
 * Inside an app's browser the sheet opens AT ONCE even before
 * get-auth-methods has answered: Google refuses sign-in there, and the
 * Instagram student who tapped in the page's first second (Oct 15) got the
 * old "Copy link" notice instead. When the answer comes back "email off",
 * the sheet closes and `onEmailOff` runs (the caller's Google path, which
 * shows that notice).
 */
export function openSheetForSignIn({ entry, onGoogle = null, onProvider = null, dest = null, title = null, lead = null,
    onEmailOff = null, everyMode = false } = {}) {
    const known = emailSignInAnswer();
    const cls = signInBrowserClass();
    if (known === true) {
        const app = cls === 'in_app' ? inAppBrowserName() : null;
        openSignInSheet({ mode: sheetModeHere(), entry, app, onGoogle, onProvider, dest, title, lead, everyMode });
        return true;
    }
    if (known !== null || cls !== 'in_app') return false;
    const answer = loadEmailSignInAvailability();
    openSignInSheet({ mode: 'in_app', entry, app: inAppBrowserName(), onGoogle, onProvider, dest, title, lead, answer, everyMode });
    const opened = sheet;
    answer.then((on) => {
        settleAnswer(opened, on ? 'email_on' : 'email_off');
        if (!on && sheet === opened && !opened.sent) {
            closeSignInSheet();
            try { onEmailOff?.(); } catch { /* the notice never breaks the close */ }
        }
    });
    return true;
}

export const getSignInSheet = () => sheet;

export function subscribeSignInSheet(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

/** The 'back' sheet after a Google sign-in that did not finish, every
 *  time (#4117; it was once a tab). It carries "Try Google again", so it
 *  never stands between the student and Google. Returns whether it opened
 *  (not when a sheet is already up). */
export function offerEmailAfterBack({ entry, onGoogle, onProvider = null, dest = null, retry = 'google', lead = null } = {}) {
    if (sheet) return false;
    openSignInSheet({ mode: 'back', entry, onGoogle, onProvider, dest, retry, lead, everyMode: Boolean(lead),
        fieldFirst: recoveryFieldFirst() });
    return true;
}

// Tests only.
export function _resetSignInSheet() {
    sheet = null;
    emit();
}
